import { SeoKeywordOpportunity } from '@/types/api/seo';

export type StepStatus = 'not_started' | 'in_progress' | 'completed' | 'needs_review';

export type PlaybookStepConfig = {
  id: string;
  label: string;
  description: string;
  opportunityTypes: string[];
  order: number;
};

export type PlaybookPhaseConfig = {
  id: string;
  number: number;
  label: string;
  subtitle: string;
  timeline: string;
  color: string;
  steps: PlaybookStepConfig[];
};

export type PlaybookStepData = PlaybookStepConfig & {
  opportunities: SeoKeywordOpportunity[];
  status: StepStatus;
};

export type PlaybookPhaseData = PlaybookPhaseConfig & {
  stepsData: PlaybookStepData[];
  completedSteps: number;
  totalSteps: number;
};

export const PLAYBOOK_PHASES: PlaybookPhaseConfig[] = [
  {
    id: 'analysis',
    number: 1,
    label: 'Analysis',
    subtitle: 'Understand what Google wants for this keyword',
    timeline: 'Day 1',
    color: '#7C3AED',
    steps: [
      {
        id: 'search_intent',
        label: 'Search Intent Match',
        description: 'Does your page format match what Google shows in search results?',
        opportunityTypes: ['search_intent_mismatch'],
        order: 1,
      },
    ],
  },
  {
    id: 'on_page',
    number: 2,
    label: 'On-Page Optimization',
    subtitle: 'Quick wins to improve rankings and CTR',
    timeline: 'Week 1-2',
    color: '#054AD9',
    steps: [
      {
        id: 'title_meta',
        label: 'Title & Meta Description',
        description: 'Optimize title tag and meta description for better CTR.',
        opportunityTypes: ['title_meta_optimization', 'low_ctr'],
        order: 1,
      },
      {
        id: 'content_upgrade',
        label: 'Content & Depth',
        description: 'Fill content gaps, add depth, and cover missing topics to match competitors.',
        opportunityTypes: ['content_depth', 'content_gap'],
        order: 2,
      },
      {
        id: 'structure_schema',
        label: 'Structure & Schema',
        description: 'Fix heading structure and add schema markup for rich snippets.',
        opportunityTypes: ['featured_snippet'],
        order: 3,
      },
      {
        id: 'technical',
        label: 'Technical & Page Speed',
        description: 'Fix Core Web Vitals and technical SEO issues.',
        opportunityTypes: ['technical_seo'],
        order: 4,
      },
    ],
  },
  {
    id: 'authority',
    number: 3,
    label: 'Authority Building',
    subtitle: 'Build link equity and topical authority',
    timeline: 'Week 3-12',
    color: '#4285F4',
    steps: [
      {
        id: 'internal_links',
        label: 'Internal Linking Campaign',
        description: 'Build internal link paths from related pages on your site.',
        opportunityTypes: ['internal_linking'],
        order: 1,
      },
      {
        id: 'cannibalization',
        label: 'Fix Cannibalization',
        description: 'Consolidate competing pages to focus authority.',
        opportunityTypes: ['cannibalization'],
        order: 2,
      },
    ],
  },
  {
    id: 'monitor',
    number: 4,
    label: 'Monitor & Iterate',
    subtitle: 'Track changes and keep content fresh',
    timeline: 'Ongoing',
    color: '#0D9D58',
    steps: [
      {
        id: 'position_tracking',
        label: 'Position & Ranking Changes',
        description: 'Monitor ranking movements and respond to changes.',
        opportunityTypes: ['striking_distance', 'quick_win', 'defend_position', 'declining_position'],
        order: 1,
      },
      {
        id: 'content_freshness',
        label: 'Content Freshness',
        description: 'Keep content updated with latest data and information.',
        opportunityTypes: ['content_freshness'],
        order: 2,
      },
    ],
  },
];

function getStepStatus(opportunities: SeoKeywordOpportunity[], analysisRun: boolean, isInTop3: boolean): StepStatus {
  if (opportunities.length === 0) {
    if (!analysisRun) return 'not_started';
    // Analysis ran but no issues found for this step
    // If already in top 3: great, this is truly complete
    // If NOT in top 3: mark as "needs review" — no detected issues doesn't mean it's optimized enough
    return isInTop3 ? 'completed' : 'needs_review';
  }
  if (opportunities.every((o) => o.status === 'completed' || o.status === 'dismissed')) return 'completed';
  if (opportunities.some((o) => o.status === 'in_progress')) return 'in_progress';
  return 'not_started';
}

export function mapOpportunitiesToPlaybook(
  opportunities: SeoKeywordOpportunity[],
  livePosition?: number | null
): { phases: PlaybookPhaseData[]; unmapped: SeoKeywordOpportunity[]; totalSteps: number; completedSteps: number } {
  // Analysis has been run if there are ANY opportunities at all
  const analysisRun = opportunities.length > 0;
  const isInTop3 = livePosition != null && livePosition <= 3;
  const mappedIds = new Set<string>();
  const phases: PlaybookPhaseData[] = [];
  let totalSteps = 0;
  let completedSteps = 0;

  for (const phase of PLAYBOOK_PHASES) {
    const stepsData: PlaybookStepData[] = [];

    for (const step of phase.steps) {
      const stepOpps = opportunities.filter((o) =>
        step.opportunityTypes.includes(o.opportunityType) && !mappedIds.has(o.id)
      );
      // Mark as mapped
      for (const o of stepOpps) mappedIds.add(o.id);

      const status = getStepStatus(stepOpps, analysisRun, isInTop3);
      stepsData.push({ ...step, opportunities: stepOpps, status });

      totalSteps++;
      if (status === 'completed') completedSteps++;
    }

    const phaseCompleted = stepsData.filter((s) => s.status === 'completed').length;
    phases.push({
      ...phase,
      stepsData,
      completedSteps: phaseCompleted,
      totalSteps: stepsData.length,
    });
  }

  // Unmapped opportunities (types not in any step)
  const unmapped = opportunities.filter((o) => !mappedIds.has(o.id));

  return { phases, unmapped, totalSteps, completedSteps };
}
